import { PureComponent } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import qs from "query-string";

import OilRequestPagination from "./sub/OilRequestPagination";
import OilRequestFilters, {
  oilRequestFilterOptions,
} from "./sub/OilRequestFilters";
import OilRequestList from "./sub/OilRequestList";
import Loader from "../../common/Loader";
import { OilRequestOrder, UserRole } from "../../utils/config";
import { getOilRequests } from "../../services/turgoil-api";
import { RouteComponentProps, withRouter } from "../../common/withRouter";
import PageTitle from "../../common/PageTitle";
import { Routes } from "../../utils/routes";

const LIMIT = 25;

/**
 * @param {number} totalOilRequests
 * @param {number} limit
 * @return {number}
 */
function getMaximumPage(totalOilRequests, limit) {
  return Math.ceil(totalOilRequests / limit) || 1;
}

type MapStateToProps = {
  user: any;
};

type MapDispatchToProps = {};

type LocationState = {
  status: string[];
  county: string[];
  oil: string[];
  orderBy: string;
  page: number;
};

type Props = MapStateToProps &
  MapDispatchToProps &
  RouteComponentProps<unknown, LocationState>;

function getAlwaysArray(query: any) {
  if (typeof query == "string") {
    query = [query];
  } else if (query == undefined) {
    query = [];
  }
  return query;
}

export function parseLocationState(state: Record<string, any>): LocationState {
  const locationState: Partial<LocationState> = {};
  locationState.status = getAlwaysArray(state?.status).filter(
    (searchStatus: string) => {
      return oilRequestFilterOptions.status.some(
        (s) => s.value === searchStatus,
      );
    },
  );
  locationState.county = getAlwaysArray(state?.county).filter(
    (searchCounty: string) => {
      return oilRequestFilterOptions.county.some(
        (s) => s.value === searchCounty,
      );
    },
  );
  locationState.oil = getAlwaysArray(state?.oil).filter((searchoil: string) => {
    return oilRequestFilterOptions.oil.some((s) => s.value === searchoil);
  });
  locationState.orderBy = oilRequestFilterOptions.orderBy.some(
    (s) => s.value === state?.orderBy,
  )
    ? state?.orderBy
    : OilRequestOrder.OilRequestRegisteredDesc;
  locationState.page = state?.page || 1;
  return locationState as LocationState;
}

type State = {
  loading: boolean;
  oilRequests: any[];
  orderBy: string;
  page: number;
  totalOilRequests: number;
};

class OilRequestsPage extends PureComponent<Props, State> {
  private unmounting: boolean = false;

  constructor(props: Props) {
    super(props);

    const { location } = this.props;
    const locationState = parseLocationState(location.state);

    this.state = {
      loading: true,
      oilRequests: [],
      orderBy: locationState.orderBy,
      page: locationState.page,
      totalOilRequests: 0,
    };

    this.fetchOilRequests(locationState);
  }

  componentWillUnmount() {
    this.unmounting = true;
  }

  private handlePageChange = (page: any) => {
    const { location } = this.props;
    const { totalOilRequests, orderBy } = this.state;
    const maximumPage = getMaximumPage(totalOilRequests, LIMIT);

    const pageInt = parseInt(page, 10);
    if (!Number.isInteger(pageInt)) {
      return;
    }

    if (pageInt < 1 || pageInt > maximumPage) {
      return;
    }

    const query: any = { ...parseLocationState(location.state) };
    query.page = pageInt;
    query.orderBy = orderBy;
    query.offset = (query.page - 1) * LIMIT;

    this.fetchOilRequests(query);
  };

  private fetchOilRequests = (query?: Record<string, any>) => {
    const { navigate } = this.props;
    this.setState({ loading: true });
    const queryParameters: any = {
      ...query,
      offset: (query.page ? query.page - 1 : 0) * LIMIT,
      limit: LIMIT,
      orderBy: query.orderBy || OilRequestOrder.OilRequestRegisteredDesc,
    };

    // We remove limit and offset because it's hardcoded
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { limit, offset, ...restQueryParams } = queryParameters;

    getOilRequests(queryParameters)
      .then((res) => {
        this.setState({
          loading: false,
          oilRequests: res.oilRequests,
          orderBy:
            queryParameters.orderBy || OilRequestOrder.OilRequestRegisteredDesc,
          page: parseInt(queryParameters.page, 10) || 1,
          totalOilRequests: res.total,
        });

        if (!this.unmounting) {
          navigate(Routes.OilRequests, {
            replace: true,
            state: query,
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  private handleSearch = (query?: Record<string, any>) => {
    this.fetchOilRequests({ ...query, page: 0 });
  };

  render() {
    const { loading, page, oilRequests, totalOilRequests } = this.state;
    const { user } = this.props;

    return (
      <>
        <Helmet>
          <title>Turgoil - Hanked</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, user-scalable=no"
          />
        </Helmet>

        <PageTitle>
          {user.role === UserRole.Bidder ? "Hanked" : "Minu hanked"}
        </PageTitle>

        <OilRequestFilters
          onSearch={this.handleSearch}
          user={this.props.user}
        />

        <div className="totalContainer">Hankeid leitud {totalOilRequests}</div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div>
              <div className="requestList">
                <OilRequestList
                  oilRequests={oilRequests}
                  onReload={this.fetchOilRequests}
                />
              </div>
              {totalOilRequests > 0 && (
                <OilRequestPagination
                  onPageChange={this.handlePageChange}
                  page={page}
                  totalOilRequests={totalOilRequests}
                  limit={LIMIT}
                />
              )}
            </div>
          </>
        )}
      </>
    );
  }
}

function mapStateToProps(state: any): MapStateToProps {
  return {
    user: state.userReducer.user,
  };
}

export default connect(mapStateToProps)(withRouter(OilRequestsPage));
